import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost, apiPut, apiDel, api } from '../lib/api';
import { formatDate, statusColor } from '../lib/format';
import { toast } from 'sonner';
import {
  Plus, Send, CheckCircle2, X, FileText, History, GitCompare, Mail, Upload, Eye, Download, File as FileIcon,
  HelpCircle, ClipboardCheck, Calculator, Sparkles, Loader2, Building2, AlertTriangle, ShieldCheck, Edit2, Trash2, RefreshCw,
} from 'lucide-react';
import DriveAttachments from '../components/DriveAttachments';
import { useFormFieldHelpers } from '../hooks/useFormFieldHelpers';
import { useUrlTab } from '../lib/hooks/useUrlState';

const TABS = ['Drawing Register', 'RFI Tracker', 'Submittals', 'Transmittals'];

// Map URL slug → tab label so sidebar deep-links auto-open the right tab.
const TAB_SLUG_MAP = {
  'drawing-register': 'Drawing Register',
  'rfi-tracker': 'RFI Tracker',
  'submittals': 'Submittals',
  'transmittals': 'Transmittals',
};

export default function Engineering() {
  // ?tab= carries either a sidebar slug (e.g. "drawings") or the tab label itself
  const [urlTab, setUrlTab] = useUrlTab('tab', '');
  const tab = TAB_SLUG_MAP[(urlTab || '').toLowerCase()] || (urlTab || 'Drawing Register');
  const setTab = (t) => setUrlTab(t === 'Drawing Register' ? '' : t);
  return (
    <div>
      <PageHeader overline="Engineering & Design" title="Drawing Register · RFI · Submittals · Transmittals"
        subtitle="Full engineering lifecycle — version-controlled drawings, RFI tracking, submittals workflow, and transmittal logs. (PEB Tonnage Calculator has moved to Tendering → PEB Tonnage.)" />
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map(t => (
            <button key={t} data-testid={`eng-tab-${t.toLowerCase().replace(/\s+/g,'-')}`} onClick={() => setTab(t)}
              className={`relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap ${tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]'}`}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#3b4872]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Drawing Register' && <DrawingRegister />}
          {tab === 'RFI Tracker' && <RFITracker />}
          {tab === 'Submittals' && <Submittals />}
          {tab === 'Transmittals' && <TransmittalLog />}
        </div>
      </div>
    </div>
  );
}

function DrawingRegister() {
  const ff = useFormFieldHelpers('drawing_upload');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => { setItems(await apiGet('/drawings')); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const drawing = await apiPost('/drawings', {
      drawing_no: fd.get('drawing_no'),
      title: fd.get('title'),
      discipline: fd.get('discipline'),
      issued_by: fd.get('issued_by'),
      current_revision: 'R0',
      current_status: 'IFR',
      revisions: [{
        id: crypto.randomUUID(),
        revision: 'R0',
        issued_date: new Date().toISOString().slice(0, 10),
        status: 'IFR',
        remarks: 'Initial issue',
        uploaded_by: 'You',
        uploaded_at: new Date().toISOString(),
      }],
    });
    toast.success(`Drawing ${drawing.drawing_no} created`);
    setCreating(false); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kpi-overline">Drawing Register</div>
          <h3 className="font-display font-bold text-base mt-1">{items.length} drawings · {items.filter(d => d.current_status === 'IFC').length} issued for construction</h3>
        </div>
        <button data-testid="dwg-new-btn" onClick={() => setCreating(true)} className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm hover:bg-[#3b4872] font-display font-semibold"><Plus size={13} /> New Drawing</button>
      </div>

      <table className="w-full dense-table">
        <thead><tr><th>Drawing No</th><th>Title</th><th>Discipline</th><th className="text-center">Revisions</th><th>Current Rev</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan="7" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No drawings yet</td></tr>}
          {items.map(d => (
            <tr key={d.id}>
              <td className="font-mono text-xs">{d.drawing_no}</td>
              <td className="max-w-md">{d.title}</td>
              <td>{d.discipline}</td>
              <td className="text-center font-display font-bold">
                <span className="tag bg-slate-100 text-slate-700 border border-slate-200 flex items-center gap-1 w-fit mx-auto"><History size={11} />{(d.revisions || []).length}</span>
              </td>
              <td className="font-mono text-xs font-bold text-[#0F172A]">{d.current_revision}</td>
              <td><span className={`tag ${d.current_status === 'IFC' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>{d.current_status}</span></td>
              <td><button data-testid="dwg-open-btn" onClick={() => setOpen(d)} className="text-xs text-[#3b4872] font-display font-semibold">Open</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {open && <DrawingDetail drawing={open} onClose={() => setOpen(null)} setOpen={setOpen} reload={load} />}
      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={create} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-xl p-5 grid grid-cols-2 gap-4">
            <div className="col-span-2"><div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">New</div><h3 className="font-display font-bold text-xl mt-1">Drawing Entry</h3></div>
            <Field label={`${ff.labelOf('drawing_no','Drawing No')}${ff.reqStar('drawing_no', true)}`}><input required={ff.isReq('drawing_no', true)} name="drawing_no" className={inputCls} placeholder="MC-CIV-003" /></Field>
            {ff.showField('discipline') && <Field label={`${ff.labelOf('discipline','Discipline')}${ff.reqStar('discipline', false)}`}>
              <select name="discipline" required={ff.isReq('discipline', false)} className={inputCls + ' bg-white'}>
                {['Civil','Structural','Architectural','Electrical','Plumbing','HVAC','Roads'].map(o => <option key={o}>{o}</option>)}
              </select>
            </Field>}
            <div className="col-span-2"><Field label={`${ff.labelOf('title','Title')}${ff.reqStar('title', true)}`}><input required={ff.isReq('title', true)} name="title" className={inputCls} /></Field></div>
            <div className="col-span-2"><Field label="Issued By"><input name="issued_by" className={inputCls} /></Field></div>
            <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-200">
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function DrawingDetail({ drawing, onClose, setOpen, reload }) {
  const revs = drawing.revisions || [];
  const [compareA, setCompareA] = useState(revs.length >= 2 ? revs[revs.length - 2].id : revs[0]?.id);
  const [compareB, setCompareB] = useState(revs[revs.length - 1]?.id);
  const [transmitting, setTransmitting] = useState(false);
  const [addingRev, setAddingRev] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !addingRev && !transmitting && !viewerFile) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [addingRev, transmitting, viewerFile, onClose]);

  const a = revs.find(r => r.id === compareA);
  const b = revs.find(r => r.id === compareB);

  const addRev = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const file = fd.get('file');
    let file_id = null;
    if (file && file.size > 0) {
      const upload = new FormData();
      upload.append('file', file);
      try {
        const r = await api.post('/upload', upload, { headers: { 'Content-Type': 'multipart/form-data' } });
        file_id = r.data.id;
      } catch { toast.error('File upload failed'); return; }
    }
    const updated = await apiPost(`/drawings/${drawing.id}/revisions`, {
      revision: fd.get('revision'),
      issued_date: fd.get('issued_date'),
      status: fd.get('status'),
      remarks: fd.get('remarks'),
      file_id,
    });
    toast.success(`Revision ${fd.get('revision')} added${file_id ? ' with file' : ''}`);
    setAddingRev(false); setOpen(updated); reload();
  };

  const transmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    await apiPost(`/drawings/${drawing.id}/transmit`, {
      sent_to: fd.get('sent_to'),
      sent_to_email: fd.get('sent_to_email'),
      remarks: fd.get('remarks'),
    });
    toast.success('Transmittal sent');
    setTransmitting(false); reload();
  };

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 sticky top-0 bg-white flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">{drawing.drawing_no} · {drawing.discipline}</div>
            <h3 className="font-display font-bold text-xl mt-1 truncate">{drawing.title}</h3>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs">
              <span className="font-mono text-slate-500">Current: {drawing.current_revision}</span>
              <span className={`tag ${drawing.current_status === 'IFC' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>{drawing.current_status}</span>
              <span className="text-slate-500">Issued by {drawing.issued_by}</span>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button data-testid="dwg-add-rev-btn" onClick={() => setAddingRev(true)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm hover:bg-[#3b4872] font-display font-semibold"><Plus size={11} /> Revision</button>
            <button data-testid="dwg-transmit-btn" onClick={() => setTransmitting(true)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658] font-display font-semibold"><Send size={11} /> Transmit</button>
            <button onClick={onClose} className="w-8 h-8 hover:bg-slate-100 rounded-sm flex items-center justify-center"><X size={15} /></button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Version Comparison */}
          <section>
            <div className="flex items-center gap-2 mb-3"><GitCompare size={14} className="text-[#3b4872]" /><div className="kpi-overline">Side-by-side Version Comparison</div></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[{ label: 'Revision A', val: compareA, set: setCompareA, data: a }, { label: 'Revision B', val: compareB, set: setCompareB, data: b }].map((p, i) => (
                <div key={i} className={`border-2 rounded-sm p-4 ${i === 0 ? 'border-slate-300' : 'border-[#3b4872]'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">{p.label}</div>
                    <select data-testid={`compare-${p.label.toLowerCase().replace(/\s/g,'-')}`} value={p.val || ''} onChange={e => p.set(e.target.value)} className="px-2 py-1 text-xs border border-slate-300 rounded-sm bg-white font-mono">
                      {revs.map(r => <option key={r.id} value={r.id}>{r.revision} — {formatDate(r.issued_date)}</option>)}
                    </select>
                  </div>
                  {p.data ? (
                    <div className="space-y-2.5 text-sm">
                      <Diff label="Status" curr={p.data.status} prev={i === 1 && a ? a.status : null} />
                      <Diff label="Issued" curr={formatDate(p.data.issued_date)} prev={i === 1 && a ? formatDate(a.issued_date) : null} />
                      <Diff label="By" curr={p.data.uploaded_by} prev={i === 1 && a ? a.uploaded_by : null} />
                      <div>
                        <div className="kpi-overline mb-1">Remarks</div>
                        <div className={`text-xs leading-relaxed p-2 rounded-sm ${i === 1 && a && a.remarks !== p.data.remarks ? 'bg-indigo-50 border-l-2 border-l-[#3b4872] -ml-0.5 pl-2' : 'bg-slate-50'}`}>
                          {p.data.remarks || '—'}
                        </div>
                      </div>
                    </div>
                  ) : <div className="text-xs text-slate-400">— select a revision —</div>}
                </div>
              ))}
            </div>
          </section>

          {/* Full Version History */}
          <section>
            <div className="flex items-center gap-2 mb-3"><History size={14} className="text-[#3b4872]" /><div className="kpi-overline">Full Version History · {revs.length} revisions</div></div>
            <div className="border border-slate-200 rounded-sm overflow-hidden">
              <table className="w-full dense-table">
                <thead><tr><th>Rev</th><th>Issued Date</th><th>Status</th><th>By</th><th>Remarks</th><th className="text-center">File</th></tr></thead>
                <tbody>
                  {revs.slice().reverse().map((r, i) => (
                    <tr key={r.id} className={i === 0 ? 'bg-indigo-50/30' : ''}>
                      <td className="font-mono font-bold">{r.revision}{i === 0 && <span className="ml-1 text-[9px] text-[#3b4872] font-display">CURRENT</span>}</td>
                      <td className="text-xs">{formatDate(r.issued_date)}</td>
                      <td><span className={`tag ${r.status === 'IFC' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>{r.status}</span></td>
                      <td className="text-xs">{r.uploaded_by}</td>
                      <td className="text-xs text-slate-600 max-w-md">{r.remarks || '—'}</td>
                      <td className="text-center">
                        {r.file_id
                          ? <button data-testid={`view-file-${r.revision}`} onClick={() => setViewerFile(r.file_id)} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm hover:bg-[#3b4872] font-display font-semibold"><Eye size={10} /> View</button>
                          : <span className="text-[10px] text-slate-300 font-mono">no file</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Drive Attachments — large reference files for this drawing */}
          <section>
            <DriveAttachments
              contextType="drawing"
              contextId={drawing.id}
              category="Engineering Drawings"
              title="Drive Reference Files · Drawings, DWGs, Scans"
              accept=".pdf,.dwg,.dxf,.dgn,.ifc,.rvt,.skp,image/*"
            />
          </section>
        </div>

        {addingRev && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <form onSubmit={addRev} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-md p-5 space-y-3">
              <div><div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">New</div><h3 className="font-display font-bold text-xl mt-1">Add Revision</h3></div>
              <Field label="Revision (e.g. R4)"><input required name="revision" className={inputCls} /></Field>
              <Field label="Issued Date"><input required type="date" name="issued_date" defaultValue={new Date().toISOString().slice(0,10)} className={inputCls} /></Field>
              <Field label="Status"><select name="status" defaultValue="IFR" className={inputCls + ' bg-white'}><option>IFR</option><option>IFC</option><option>Superseded</option></select></Field>
              <Field label="Remarks"><textarea name="remarks" rows={3} className={inputCls} /></Field>
              <Field label="Drawing File (PDF / DWG / Image)">
                <input data-testid="rev-file-input" type="file" name="file" accept=".pdf,.dwg,.dxf,image/*" className="block w-full text-xs file:mr-2 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-[#3b4872] file:text-white file:font-display file:font-semibold file:cursor-pointer hover:file:bg-[#2d3856]" />
                <div className="text-[10px] text-slate-500 mt-1">Optional — file is stored encrypted and only authorised users can view.</div>
              </Field>
              <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                <button type="button" onClick={() => setAddingRev(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold">Add Revision</button>
              </div>
            </form>
          </div>
        )}

        {transmitting && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <form onSubmit={transmit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-md p-5 space-y-3">
              <div><div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">Send</div><h3 className="font-display font-bold text-xl mt-1">Transmit Drawing</h3>
                <div className="text-xs text-slate-500 mt-1">{drawing.drawing_no} · Rev {drawing.current_revision}</div>
              </div>
              <Field label="Sent To (Recipient name)"><input required name="sent_to" className={inputCls} /></Field>
              <Field label="Recipient Email"><input type="email" name="sent_to_email" className={inputCls} placeholder="optional" /></Field>
              <Field label="Remarks / Cover note"><textarea name="remarks" rows={3} className={inputCls} /></Field>
              <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                <button type="button" onClick={() => setTransmitting(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold flex items-center gap-1"><Send size={12} /> Send Transmittal</button>
              </div>
            </form>
          </div>
        )}
        {viewerFile && <FileViewer fileId={viewerFile} onClose={() => setViewerFile(null)} />}
      </div>
    </div>
  );
}

function Diff({ label, curr, prev }) {
  const changed = prev !== null && prev !== undefined && prev !== curr;
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
      <div className="kpi-overline">{label}</div>
      <div className={`text-sm font-display font-semibold ${changed ? 'text-[#3b4872]' : ''}`}>
        {curr || '—'}{changed && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#3b4872]">changed</span>}
      </div>
    </div>
  );
}

function FileViewer({ fileId, onClose }) {
  const [url, setUrl] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let active = true;
    let blobUrl;
    (async () => {
      try {
        const m = await apiGet(`/files/${fileId}/meta`);
        if (!active) return;
        setMeta(m);
        const r = await api.get(`/files/${fileId}/download`, { responseType: 'blob' });
        if (!active) return;
        blobUrl = URL.createObjectURL(r.data);
        setUrl(blobUrl);
      } catch { toast.error('Failed to load file'); onClose(); }
    })();
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [fileId, onClose]);

  const isImage = meta?.content_type?.startsWith('image/');
  const isPdf = meta?.content_type === 'application/pdf' || meta?.original_filename?.toLowerCase().endsWith('.pdf');

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-5xl h-[85vh] flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileIcon size={16} className="text-[#3b4872]" />
            <div>
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">Drawing File</div>
              <div className="font-display font-bold text-sm truncate max-w-[400px]">{meta?.original_filename || 'Loading…'}</div>
            </div>
          </div>
          <div className="flex gap-2">
            {url && <a data-testid="file-download" href={url} download={meta?.original_filename} className="flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50 font-display font-semibold"><Download size={12} /> Download</a>}
            <button onClick={onClose} className="w-8 h-8 hover:bg-slate-100 rounded-sm flex items-center justify-center"><X size={15} /></button>
          </div>
        </div>
        <div className="flex-1 bg-slate-100 flex items-center justify-center overflow-auto">
          {!url && <div className="text-sm text-slate-500 font-mono">Loading file…</div>}
          {url && isImage && <img src={url} alt="" className="max-w-full max-h-full" />}
          {url && isPdf && <iframe data-testid="file-pdf-frame" src={url} className="w-full h-full border-0" title="drawing" />}
          {url && !isImage && !isPdf && (
            <div className="text-center p-8">
              <FileIcon size={56} className="mx-auto text-slate-400 mb-3" />
              <div className="font-display font-bold mb-1">{meta?.original_filename}</div>
              <div className="text-xs text-slate-500 mb-4">{meta?.content_type} · {meta?.size ? (meta.size / 1024).toFixed(1) + ' KB' : ''}</div>
              <a href={url} download={meta?.original_filename} className="inline-flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white rounded-sm font-display font-semibold text-sm"><Download size={13} /> Download to view</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TransmittalLog() {
  const [items, setItems] = useState([]);
  const [acking, setAcking] = useState(null);
  // Automation #7 — acknowledgement SLA breaches (7-day window).
  const [sla, setSla] = useState(null);

  const load = async () => { setItems(await apiGet('/transmittals')); };
  useEffect(() => {
    load();
    apiGet('/automation/transmittal-slas', { sla_days: 7 }).then(setSla).catch(() => {});
  }, []);

  const ack = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    await apiPut(`/transmittals/${acking.id}/acknowledge`, {
      acknowledged_by: fd.get('acknowledged_by'),
      remarks: fd.get('remarks'),
    });
    toast.success('Transmittal acknowledged');
    setAcking(null); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kpi-overline">Transmittal Log</div>
          <h3 className="font-display font-bold text-base mt-1">{items.length} transmittals · {items.filter(t => t.status === 'Acknowledged').length} acknowledged · {items.filter(t => t.status === 'Sent').length} pending</h3>
        </div>
        {sla && sla.count > 0 && (
          <span className="px-2.5 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-sm text-xs font-display font-bold" data-testid="transmittal-sla-badge">
            {sla.count} past {sla.sla_days}-day acknowledgement SLA
          </span>
        )}
      </div>

      <div className="space-y-3">
        {items.length === 0 && <div className="text-center py-12 border border-dashed border-slate-200 rounded-sm text-slate-400 text-xs uppercase tracking-widest font-display">No transmittals yet · transmit a drawing to log here</div>}
        {items.map(t => (
          <div key={t.id} className={`bg-white border rounded-sm p-4 ${t.status === 'Acknowledged' ? 'border-emerald-200' : 'border-amber-300'}`}>
            <div className="flex items-start gap-3 flex-wrap">
              <div className={`w-10 h-10 rounded-sm flex items-center justify-center shrink-0 ${t.status === 'Acknowledged' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {t.status === 'Acknowledged' ? <CheckCircle2 size={18} /> : <Send size={16} />}
              </div>
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="font-mono text-xs font-bold text-[#0F172A]">{t.transmittal_no}</span>
                  <span className={`tag ${t.status === 'Acknowledged' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>{t.status}</span>
                </div>
                <div className="font-display font-semibold">{t.drawing_no} <span className="text-slate-400">· Rev {t.revision}</span></div>
                <div className="text-xs text-slate-600 truncate max-w-md">{t.drawing_title}</div>
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-2 flex-wrap">
                  <Mail size={11} />
                  <span><span className="text-[#0F172A] font-medium">To:</span> {t.sent_to}</span>
                  {t.sent_to_email && <span className="font-mono">({t.sent_to_email})</span>}
                </div>
                {t.remarks && <div className="text-xs text-slate-600 mt-1 italic">"{t.remarks}"</div>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100">
                  <div>
                    <div className="kpi-overline mb-0.5">Sent</div>
                    <div className="text-xs"><span className="font-mono">{formatDate(t.sent_at)}</span> · by <span className="font-medium">{t.sent_by}</span></div>
                  </div>
                  {t.acknowledged_at ? (
                    <div>
                      <div className="kpi-overline text-emerald-700 mb-0.5">Acknowledged</div>
                      <div className="text-xs"><span className="font-mono">{formatDate(t.acknowledged_at)}</span> · by <span className="font-medium">{t.acknowledged_by}</span></div>
                      {t.ack_remarks && <div className="text-xs text-slate-600 italic mt-1">"{t.ack_remarks}"</div>}
                    </div>
                  ) : (
                    <div>
                      <div className="kpi-overline text-amber-700 mb-0.5">Pending Acknowledgement</div>
                      <button data-testid="ack-btn" onClick={() => setAcking(t)} className="text-xs px-3 py-1 mt-1 bg-emerald-600 text-white rounded-sm font-display font-semibold hover:bg-emerald-700 flex items-center gap-1"><CheckCircle2 size={11} /> Mark Acknowledged</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {acking && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={ack} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-md p-5 space-y-3">
            <div><div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">Acknowledge</div><h3 className="font-display font-bold text-xl mt-1">{acking.transmittal_no}</h3>
              <div className="text-xs text-slate-500 mt-1">{acking.drawing_no} · Rev {acking.revision}</div>
            </div>
            <Field label="Acknowledged By"><input required name="acknowledged_by" className={inputCls} /></Field>
            <Field label="Remarks (e.g. Approved without comments)"><textarea name="remarks" rows={3} className={inputCls} /></Field>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <button type="button" onClick={() => setAcking(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-sm font-display font-semibold hover:bg-emerald-700">Confirm Ack</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// RFI TRACKER — Iter 101 · Engineering Lifecycle
// ════════════════════════════════════════════════════════════
const DISCIPLINES = [
  'PEB · Primary Frame', 'PEB · Secondary Frame', 'PEB · Sheeting', 'PEB · Bracing',
  'PEB · Mezzanine', 'PEB · Crane Beam', 'PEB · Anchor Bolt',
  'Civil', 'Structural', 'Architectural', 'Electrical', 'Plumbing', 'HVAC',
  'Fire Fighting', 'MEP', 'Instrumentation', 'Other',
];
const RFI_SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];
const RFI_STATUSES = ['Open', 'Sent', 'Responded', 'Closed', 'Reopened'];

const severityColor = (s) => ({
  Critical: 'bg-red-100 text-red-700',
  High: 'bg-orange-100 text-orange-700',
  Medium: 'bg-amber-100 text-amber-700',
  Low: 'bg-slate-100 text-slate-700',
})[s] || 'bg-slate-100 text-slate-700';

const rfiStatusColor = (s) => ({
  Open: 'bg-blue-100 text-blue-700',
  Sent: 'bg-indigo-100 text-indigo-700',
  Responded: 'bg-emerald-100 text-emerald-700',
  Closed: 'bg-slate-100 text-slate-700',
  Reopened: 'bg-red-100 text-red-700',
})[s] || 'bg-slate-100 text-slate-700';

function RFITracker() {
  const [rfis, setRfis] = useState([]);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [opened, setOpened] = useState(null);

  const load = async () => {
    try {
      const p = projectFilter ? `?project_id=${projectFilter}` : '';
      const [rs, st, ps] = await Promise.all([
        apiGet(`/engineering/rfi${p}`),
        apiGet(`/engineering/rfi/stats${p}`),
        apiGet('/projects').catch(() => []),
      ]);
      setRfis(rs.rfis || []);
      setStats(st);
      const projList = Array.isArray(ps) ? ps : (ps?.projects || []);
      setProjects(projList);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectFilter]);

  const filtered = useMemo(() => {
    let list = rfis;
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) =>
        (r.title || '').toLowerCase().includes(q) ||
        (r.rfi_no || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [rfis, statusFilter, search]);

  return (
    <div>
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="kpi-overline">RFI Tracker</div>
          <h3 className="font-display font-bold text-base mt-1">
            {stats?.total || 0} RFIs · <span className="text-blue-700">{stats?.open || 0} open</span>
            {stats?.overdue > 0 && <> · <span className="text-red-700">{stats.overdue} overdue</span></>}
          </h3>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-300 rounded-sm bg-white font-display" data-testid="rfi-project-filter">
            <option value="">All Projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code || p.project_code || p.name}</option>)}
          </select>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search RFI…" className="px-2 py-1.5 text-xs border border-slate-300 rounded-sm" data-testid="rfi-search" />
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm hover:bg-[#3b4872] font-display font-semibold" data-testid="rfi-new-btn">
            <Plus size={13} /> New RFI
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="rfi-stats">
          <SmallStat label="Total" value={stats.total} />
          <SmallStat label="Open" value={stats.open} tone="blue" />
          <SmallStat label="Overdue" value={stats.overdue} tone="red" />
          <SmallStat label="Critical" value={stats.by_severity?.Critical || 0} tone="red" />
          <SmallStat label="Responded" value={stats.by_status?.Responded || 0} tone="emerald" />
        </div>
      )}

      <div className="flex gap-2 mb-3 flex-wrap" data-testid="rfi-status-chips">
        <Chip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>All ({rfis.length})</Chip>
        {RFI_STATUSES.map((s) => (
          <Chip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} testid={`rfi-chip-${s}`}>
            {s} ({rfis.filter((r) => r.status === s).length})
          </Chip>
        ))}
      </div>

      <table className="w-full dense-table">
        <thead>
          <tr>
            <th>RFI No.</th>
            <th>Title</th>
            <th>Discipline</th>
            <th>Severity</th>
            <th>Required By</th>
            <th>Status</th>
            <th>Cost ₹</th>
            <th>Days</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-slate-400 text-xs uppercase tracking-widest font-display">No RFIs match filter</td></tr>}
          {filtered.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setOpened(r)} data-testid={`rfi-row-${r.id}`}>
              <td className="font-mono text-xs font-bold">{r.rfi_no}</td>
              <td className="max-w-md"><div className="truncate font-display font-semibold">{r.title}</div></td>
              <td className="text-xs">{r.discipline}</td>
              <td><span className={`tag ${severityColor(r.severity)} text-[10px]`}>{r.severity}</span></td>
              <td className="text-xs">{r.required_by || '—'}</td>
              <td><span className={`tag ${rfiStatusColor(r.status)} text-[10px]`}>{r.status}</span></td>
              <td className="text-right text-xs">{r.cost_impact_inr ? `₹${Number(r.cost_impact_inr).toLocaleString('en-IN')}` : '—'}</td>
              <td className="text-right text-xs">{r.schedule_impact_days || '—'}</td>
              <td onClick={(e) => e.stopPropagation()}><button className="text-xs text-[#3b4872] font-display font-semibold" onClick={() => setOpened(r)}>Open</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {showForm && <RFIForm projects={projects} editing={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); }} />}
      {opened && <RFIDetail rfi={opened} onClose={() => setOpened(null)} onChange={() => { load(); setOpened(null); }} />}
    </div>
  );
}

function RFIForm({ projects, editing, onClose, onSaved }) {
  const [form, setForm] = useState({
    project_id: editing?.project_id || projects[0]?.id || '',
    title: editing?.title || '',
    description: editing?.description || '',
    discipline: editing?.discipline || DISCIPLINES[0],
    severity: editing?.severity || 'Medium',
    required_by: editing?.required_by || '',
    routed_to_consultant: editing?.routed_to_consultant || '',
    routed_to_client: editing?.routed_to_client || '',
    cost_impact_inr: editing?.cost_impact_inr || '',
    schedule_impact_days: editing?.schedule_impact_days || '',
    cost_claim_raised: editing?.cost_claim_raised || false,
    time_extension_requested: editing?.time_extension_requested || false,
    drawing_no: editing?.drawing_no || '',
  });
  const [aiBusy, setAiBusy] = useState(false);
  const [siteObs, setSiteObs] = useState('');
  const [showAiBox, setShowAiBox] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleAiDraft = async () => {
    if (!siteObs.trim()) { toast.error('Paste a site observation first'); return; }
    setAiBusy(true);
    try {
      const out = await apiPost('/engineering/rfi/ai-draft', { site_observation: siteObs, discipline: form.discipline });
      setForm((f) => ({ ...f, title: out.title || f.title, description: out.description || f.description, discipline: out.discipline || f.discipline, severity: out.severity || f.severity }));
      toast.success('AI-drafted RFI ready · review and adjust');
      setShowAiBox(false);
    } catch (e) { toast.error('AI draft failed'); }
    setAiBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { toast.error('Pick a project'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost_impact_inr: form.cost_impact_inr === '' ? null : Number(form.cost_impact_inr),
        schedule_impact_days: form.schedule_impact_days === '' ? null : Number(form.schedule_impact_days),
      };
      if (editing) await apiPut(`/engineering/rfi/${editing.id}`, payload);
      else await apiPost('/engineering/rfi', payload);
      toast.success(editing ? 'RFI updated' : 'RFI created');
      onSaved();
    } catch (e) { toast.error('Save failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 sticky top-0 bg-white flex items-center justify-between z-10">
          <div>
            <div className="kpi-overline">{editing ? 'Edit RFI' : 'New RFI'}</div>
            <h3 className="font-display font-bold text-xl mt-1">{editing?.rfi_no || 'Raise a Request For Information'}</h3>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 hover:bg-slate-100 rounded-sm flex items-center justify-center"><X size={15} /></button>
        </div>

        <div className="p-5 space-y-4">
          {!editing && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="rfi-ai-helper">
              <div className="flex items-center justify-between">
                <div className="text-xs text-amber-900 flex items-center gap-2"><Sparkles size={14} /> Got a quick site observation? Let Ellora AI draft the RFI for you.</div>
                <button type="button" onClick={() => setShowAiBox(!showAiBox)} className="text-xs text-amber-900 hover:underline font-display font-semibold" data-testid="rfi-ai-toggle">{showAiBox ? 'Hide' : 'Try AI Draft →'}</button>
              </div>
              {showAiBox && (
                <div className="mt-3">
                  <textarea value={siteObs} onChange={(e) => setSiteObs(e.target.value)}
                    placeholder='e.g. "On column at grid B-3 there is dimension mismatch. Drawing says 24mm anchor bolt but spec says 20mm. Need clarity before pouring concrete."'
                    rows={3} className={inputCls + ' bg-white'} data-testid="rfi-ai-input" />
                  <div className="flex justify-end mt-2">
                    <button type="button" onClick={handleAiDraft} disabled={aiBusy} className="px-3 py-1.5 bg-[#3b4872] text-white rounded-sm text-xs font-display font-semibold hover:bg-[#2d3856] disabled:opacity-50 flex items-center gap-2" data-testid="rfi-ai-draft-btn">
                      {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Draft with AI
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Project *">
              <select required value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className={inputCls + ' bg-white'} data-testid="rfi-project">
                {projects.map((p) => <option key={p.id} value={p.id}>{p.code || p.project_code || p.name}</option>)}
              </select>
            </Field>
            <Field label="Discipline *">
              <select required value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} className={inputCls + ' bg-white'} data-testid="rfi-discipline">
                {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <div className="col-span-2">
              <Field label="Title *">
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="rfi-title" />
              </Field>
            </div>
            <div className="col-span-2">
              <Field label="Description *">
                <textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid="rfi-description" />
              </Field>
            </div>
            <Field label="Severity *">
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className={inputCls + ' bg-white'} data-testid="rfi-severity">
                {RFI_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Required By">
              <input type="date" value={form.required_by} onChange={(e) => setForm({ ...form, required_by: e.target.value })} className={inputCls} data-testid="rfi-required-by" />
            </Field>
            <Field label="Drawing No.">
              <input value={form.drawing_no} onChange={(e) => setForm({ ...form, drawing_no: e.target.value })} className={inputCls} placeholder="e.g. PEB-001-R2" />
            </Field>
            <Field label="Routed to Consultant">
              <input value={form.routed_to_consultant} onChange={(e) => setForm({ ...form, routed_to_consultant: e.target.value })} className={inputCls} placeholder="Consultant name" />
            </Field>
            <Field label="Cost Impact (₹)">
              <input type="number" value={form.cost_impact_inr} onChange={(e) => setForm({ ...form, cost_impact_inr: e.target.value })} className={inputCls} data-testid="rfi-cost-impact" />
            </Field>
            <Field label="Schedule Impact (Days)">
              <input type="number" value={form.schedule_impact_days} onChange={(e) => setForm({ ...form, schedule_impact_days: e.target.value })} className={inputCls} data-testid="rfi-schedule-impact" />
            </Field>
            <div className="col-span-2 flex gap-6 pt-2">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={form.cost_claim_raised} onChange={(e) => setForm({ ...form, cost_claim_raised: e.target.checked })} className="w-4 h-4 accent-[#3b4872]" data-testid="rfi-cost-claim" />
                Cost claim raised
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={form.time_extension_requested} onChange={(e) => setForm({ ...form, time_extension_requested: e.target.checked })} className="w-4 h-4 accent-[#3b4872]" data-testid="rfi-time-extension" />
                Time extension requested
              </label>
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50" data-testid="rfi-save-btn">
            {saving ? 'Saving…' : (editing ? 'Update' : 'Create RFI')}
          </button>
        </div>
      </form>
    </div>
  );
}

function RFIDetail({ rfi, onClose, onChange }) {
  const [resp, setResp] = useState('');
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const act = async (fn, label) => {
    setBusy(label);
    try { await fn(); toast.success(`${label} done`); onChange(); }
    catch (e) { toast.error(`${label} failed`); }
    setBusy(null);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 sticky top-0 bg-white flex items-start justify-between z-10">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">{rfi.rfi_no} · {rfi.discipline}</div>
            <h3 className="font-display font-bold text-xl mt-1">{rfi.title}</h3>
            <div className="flex gap-2 mt-2 flex-wrap">
              <span className={`tag ${severityColor(rfi.severity)} text-[10px]`}>{rfi.severity}</span>
              <span className={`tag ${rfiStatusColor(rfi.status)} text-[10px]`}>{rfi.status}</span>
              {rfi.cost_claim_raised && <span className="tag bg-orange-100 text-orange-700 text-[10px]">Cost Claim</span>}
              {rfi.time_extension_requested && <span className="tag bg-orange-100 text-orange-700 text-[10px]">Time Extension</span>}
              {rfi.required_by && <span className="text-[11px] text-slate-500">Due {rfi.required_by}</span>}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 hover:bg-slate-100 rounded-sm flex items-center justify-center"><X size={15} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="kpi-overline mb-1">Description</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{rfi.description}</div>
          </div>
          {(rfi.cost_impact_inr || rfi.schedule_impact_days) && (
            <div className="grid grid-cols-2 gap-3">
              {rfi.cost_impact_inr != null && <SmallStat label="Cost Impact" value={`₹${Number(rfi.cost_impact_inr).toLocaleString('en-IN')}`} tone="red" />}
              {rfi.schedule_impact_days != null && <SmallStat label="Schedule Impact" value={`${rfi.schedule_impact_days} days`} tone="amber" />}
            </div>
          )}
          {rfi.response && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
              <div className="kpi-overline text-emerald-700 mb-1">Response · {rfi.responded_by} · {rfi.response_date?.slice(0, 10)}</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{rfi.response}</div>
            </div>
          )}
          {!rfi.response && rfi.status !== 'Closed' && (
            <div>
              <div className="kpi-overline mb-1">Add Response</div>
              <textarea value={resp} onChange={(e) => setResp(e.target.value)} rows={3} className={inputCls} placeholder="Consultant's response…" data-testid="rfi-response-input" />
              <button onClick={() => act(async () => { await apiPost(`/engineering/rfi/${rfi.id}/respond`, { response: resp }); }, 'Respond')}
                disabled={!resp.trim() || busy} className="mt-2 px-3 py-1.5 bg-emerald-600 text-white rounded-sm text-xs font-display font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="rfi-respond-btn">
                {busy === 'Respond' ? 'Saving…' : 'Record Response'}
              </button>
            </div>
          )}
          {rfi.events?.length > 0 && (
            <div>
              <div className="kpi-overline mb-2">Audit Trail</div>
              <ol className="space-y-1">
                {rfi.events.map((ev, i) => (
                  <li key={i} className="text-xs text-slate-600 flex items-center gap-2">
                    <span className="text-slate-400 font-mono text-[10px]">{ev.at?.slice(0, 16)?.replace('T', ' ')}</span>
                    <span className="font-display font-bold uppercase tracking-wider text-[10px] text-[#3b4872]">{ev.type}</span>
                    <span>by {ev.by}</span>
                    {ev.comment && <span className="text-slate-500">— {ev.comment}</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white flex-wrap">
          {(rfi.status === 'Open' || rfi.status === 'Reopened') && (
            <button onClick={() => act(async () => { await apiPost(`/engineering/rfi/${rfi.id}/send`); }, 'Send')} className="px-3 py-1.5 bg-indigo-600 text-white rounded-sm text-xs font-display font-semibold hover:bg-indigo-700 flex items-center gap-1" data-testid="rfi-send-btn">
              <Send size={11} /> Send to Consultant
            </button>
          )}
          {rfi.status === 'Responded' && (
            <button onClick={() => act(async () => { await apiPost(`/engineering/rfi/${rfi.id}/close`); }, 'Close')} className="px-3 py-1.5 bg-slate-700 text-white rounded-sm text-xs font-display font-semibold hover:bg-slate-800 flex items-center gap-1" data-testid="rfi-close-btn">
              <CheckCircle2 size={11} /> Close
            </button>
          )}
          {rfi.status === 'Closed' && (
            <button onClick={() => act(async () => { await apiPost(`/engineering/rfi/${rfi.id}/reopen`); }, 'Reopen')} className="px-3 py-1.5 bg-red-600 text-white rounded-sm text-xs font-display font-semibold hover:bg-red-700 flex items-center gap-1" data-testid="rfi-reopen-btn">
              <RefreshCw size={11} /> Reopen
            </button>
          )}
          <button onClick={() => act(async () => { await apiDel(`/engineering/rfi/${rfi.id}`); }, 'Delete')} className="px-3 py-1.5 bg-white border border-red-300 text-red-600 rounded-sm text-xs font-display font-semibold hover:bg-red-50 flex items-center gap-1">
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// SUBMITTALS
// ════════════════════════════════════════════════════════════
const SUBMITTAL_TYPES = [
  'Material Approval Request (MAR)', 'Method Statement', 'Shop Drawing',
  'Mockup Approval', 'Inspection & Test Plan (ITP)', 'Calculation Sheet', 'Other',
];
const SUBMITTAL_STATUSES = ['Draft', 'Submitted', 'Under Review', 'Approved', 'Approved With Comments', 'Resubmit', 'Rejected', 'Closed'];

const subStatusColor = (s) => ({
  Draft: 'bg-slate-100 text-slate-700',
  Submitted: 'bg-blue-100 text-blue-700',
  'Under Review': 'bg-indigo-100 text-indigo-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  'Approved With Comments': 'bg-amber-100 text-amber-700',
  Resubmit: 'bg-orange-100 text-orange-700',
  Rejected: 'bg-red-100 text-red-700',
  Closed: 'bg-slate-100 text-slate-700',
})[s] || 'bg-slate-100 text-slate-700';

function Submittals() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [list, ps] = await Promise.all([
        apiGet('/engineering/submittals'),
        apiGet('/projects').catch(() => []),
      ]);
      setItems(list.submittals || []);
      const projList = Array.isArray(ps) ? ps : (ps?.projects || []);
      setProjects(projList);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const filtered = statusFilter === 'all' ? items : items.filter((s) => s.status === statusFilter);

  return (
    <div>
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="kpi-overline">Submittals</div>
          <h3 className="font-display font-bold text-base mt-1">{items.length} submittals · {items.filter((s) => s.status === 'Approved').length} approved</h3>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm hover:bg-[#3b4872] font-display font-semibold" data-testid="sub-new-btn">
          <Plus size={13} /> New Submittal
        </button>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap" data-testid="sub-status-chips">
        <Chip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>All ({items.length})</Chip>
        {SUBMITTAL_STATUSES.map((s) => (
          <Chip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)}>{s} ({items.filter((x) => x.status === s).length})</Chip>
        ))}
      </div>

      <table className="w-full dense-table">
        <thead>
          <tr>
            <th>Submittal No.</th>
            <th>Title</th>
            <th>Type</th>
            <th>Discipline</th>
            <th>Required By</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-slate-400 text-xs uppercase tracking-widest font-display">No submittals yet</td></tr>}
          {filtered.map((s) => (
            <tr key={s.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => { setEditing(s); setShowForm(true); }} data-testid={`sub-row-${s.id}`}>
              <td className="font-mono text-xs font-bold">{s.submittal_no}</td>
              <td className="max-w-md"><div className="truncate font-display font-semibold">{s.title}</div></td>
              <td className="text-xs">{s.submittal_type}</td>
              <td className="text-xs">{s.discipline || '—'}</td>
              <td className="text-xs">{s.required_by || '—'}</td>
              <td><span className={`tag ${subStatusColor(s.status)} text-[10px]`}>{s.status}</span></td>
              <td><button className="text-xs text-[#3b4872] font-display font-semibold">Open</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {showForm && <SubmittalForm projects={projects} editing={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); }} />}
    </div>
  );
}

function SubmittalForm({ projects, editing, onClose, onSaved }) {
  const [form, setForm] = useState({
    project_id: editing?.project_id || projects[0]?.id || '',
    title: editing?.title || '',
    description: editing?.description || '',
    submittal_type: editing?.submittal_type || SUBMITTAL_TYPES[0],
    discipline: editing?.discipline || DISCIPLINES[0],
    status: editing?.status || 'Draft',
    required_by: editing?.required_by || '',
    routed_to_consultant: editing?.routed_to_consultant || '',
    consultant_comments: editing?.consultant_comments || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { toast.error('Pick a project'); return; }
    setSaving(true);
    try {
      if (editing) await apiPut(`/engineering/submittals/${editing.id}`, form);
      else await apiPost('/engineering/submittals', form);
      toast.success(editing ? 'Submittal updated' : 'Submittal created');
      onSaved();
    } catch (e) { toast.error('Save failed'); }
    setSaving(false);
  };

  const del = async () => {
    if (!editing || !window.confirm(`Delete ${editing.submittal_no}?`)) return;
    try { await apiDel(`/engineering/submittals/${editing.id}`); toast.success('Deleted'); onSaved(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 sticky top-0 bg-white flex items-center justify-between z-10">
          <div>
            <div className="kpi-overline">{editing ? 'Edit Submittal' : 'New Submittal'}</div>
            <h3 className="font-display font-bold text-xl mt-1">{editing?.submittal_no || 'Create Submittal'}</h3>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 hover:bg-slate-100 rounded-sm flex items-center justify-center"><X size={15} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <Field label="Project *">
            <select required value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className={inputCls + ' bg-white'} data-testid="sub-project">
              {projects.map((p) => <option key={p.id} value={p.id}>{p.code || p.project_code || p.name}</option>)}
            </select>
          </Field>
          <Field label="Type *">
            <select required value={form.submittal_type} onChange={(e) => setForm({ ...form, submittal_type: e.target.value })} className={inputCls + ' bg-white'} data-testid="sub-type">
              {SUBMITTAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <div className="col-span-2"><Field label="Title *"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="sub-title" /></Field></div>
          <div className="col-span-2"><Field label="Description"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} /></Field></div>
          <Field label="Discipline">
            <select value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} className={inputCls + ' bg-white'}>
              {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Required By"><input type="date" value={form.required_by} onChange={(e) => setForm({ ...form, required_by: e.target.value })} className={inputCls} /></Field>
          <Field label="Routed to Consultant"><input value={form.routed_to_consultant} onChange={(e) => setForm({ ...form, routed_to_consultant: e.target.value })} className={inputCls} /></Field>
          {editing && (
            <Field label="Status">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls + ' bg-white'} data-testid="sub-status">
                {SUBMITTAL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          )}
          {editing && (
            <div className="col-span-2"><Field label="Consultant Comments"><textarea rows={2} value={form.consultant_comments} onChange={(e) => setForm({ ...form, consultant_comments: e.target.value })} className={inputCls} placeholder="Comments from consultant review" /></Field></div>
          )}
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-between gap-2 sticky bottom-0 bg-white">
          {editing && <button type="button" onClick={del} className="px-3 py-1.5 bg-white border border-red-300 text-red-600 rounded-sm text-xs font-display font-semibold hover:bg-red-50 flex items-center gap-1"><Trash2 size={11} /> Delete</button>}
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50" data-testid="sub-save-btn">{saving ? 'Saving…' : (editing ? 'Update' : 'Create')}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// PEB TONNAGE CALCULATOR
// ════════════════════════════════════════════════════════════
const WIND_ZONES = [
  { label: 'Zone I (33 m/s) — Hyderabad, Bangalore', value: 33 },
  { label: 'Zone II (39 m/s) — Pune, Nashik, Indore', value: 39 },
  { label: 'Zone III (44 m/s) — Mumbai, Ahmedabad', value: 44 },
  { label: 'Zone IV (47 m/s) — Chennai, Kolkata', value: 47 },
  { label: 'Zone V (50 m/s) — Coastal Gujarat, Odisha', value: 50 },
  { label: 'Zone VI (55 m/s) — Cyclone-prone coastal', value: 55 },
];

function PEBTonnageCalc() {
  const [s, setS] = useState({
    length: 60, width: 30, eave_height: 9, ridge_height: 11.5,
    clear_span: 30, bay_spacing: 6, roof_slope_deg: 5,
    eot_crane_capacity: 0, hanging_load_kgsqm: 25, wind_speed: 39,
    mezzanine_area_sqm: 0, mezzanine_loading_kgsqm: 500,
    sheeting_roof_grade: 'TR 0.5mm', wall_height_open_pct: 0,
  });

  const calc = useMemo(() => {
    const builtUp = s.length * s.width;
    const wallPerimeter = 2 * (s.length + s.width);
    const wallArea = wallPerimeter * s.eave_height * (1 - s.wall_height_open_pct / 100);
    const roofArea = builtUp / Math.cos((s.roof_slope_deg * Math.PI) / 180);

    let primaryRate = 10;
    primaryRate += Math.max(0, s.clear_span - 20) * 0.55;
    primaryRate += Math.max(0, s.eave_height - 6) * 0.45;
    primaryRate += s.eot_crane_capacity * 0.9;
    primaryRate += Math.max(0, s.wind_speed - 39) * 0.18;
    primaryRate += s.hanging_load_kgsqm * 0.04;

    let secondaryRate = 5;
    secondaryRate += Math.max(0, s.wind_speed - 39) * 0.08;
    secondaryRate += Math.max(0, s.eave_height - 6) * 0.18;

    let bracingRate = 1.5;
    bracingRate += Math.max(0, s.wind_speed - 39) * 0.06;

    const mezzKgPerSqm = 35 + s.mezzanine_loading_kgsqm * 0.018;
    const mezzWeight = s.mezzanine_area_sqm * mezzKgPerSqm;

    const sheetWeightPerSqm = s.sheeting_roof_grade.includes('0.5mm') ? 5.5
      : s.sheeting_roof_grade.includes('0.47') ? 5.2
      : s.sheeting_roof_grade.includes('0.6') ? 6.5 : 5.5;
    const sheetingWeight = (roofArea + wallArea) * sheetWeightPerSqm;

    const primary = builtUp * primaryRate;
    const secondary = builtUp * secondaryRate;
    const bracing = builtUp * bracingRate;
    const connections = (primary + secondary) * 0.07;
    const total = primary + secondary + bracing + mezzWeight + sheetingWeight + connections;

    return {
      builtUp, wallArea, roofArea,
      primaryRate, secondaryRate, bracingRate,
      primary, secondary, bracing, mezzWeight, sheetingWeight, connections, total,
      totalSteel: primary + secondary + bracing + mezzWeight + connections,
    };
  }, [s]);

  const update = (k, v) => setS({ ...s, [k]: Number(v) || 0 });
  const updateStr = (k, v) => setS({ ...s, [k]: v });
  const formatT = (kg) => `${(kg / 1000).toFixed(2)} T`;

  return (
    <div data-testid="peb-calc">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="kpi-overline">PEB Tonnage Calculator</div>
          <h3 className="font-display font-bold text-base mt-1">Auto-estimate steel tonnage from a specification sheet</h3>
          <p className="text-xs text-slate-600 mt-1">Industry rule-of-thumb for tender budgeting. For final design, refer to IS-800 / IS-875.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 space-y-4">
          <Section title="Building Dimensions">
            <NumInput label="Length (m)" v={s.length} onChange={(v) => update('length', v)} testid="peb-length" />
            <NumInput label="Width (m)" v={s.width} onChange={(v) => update('width', v)} testid="peb-width" />
            <NumInput label="Eave Height (m)" v={s.eave_height} onChange={(v) => update('eave_height', v)} testid="peb-eave" />
            <NumInput label="Ridge Height (m)" v={s.ridge_height} onChange={(v) => update('ridge_height', v)} />
            <NumInput label="Clear Span (m)" v={s.clear_span} onChange={(v) => update('clear_span', v)} testid="peb-clear-span" />
            <NumInput label="Bay Spacing (m)" v={s.bay_spacing} onChange={(v) => update('bay_spacing', v)} />
            <NumInput label="Roof Slope (°)" v={s.roof_slope_deg} onChange={(v) => update('roof_slope_deg', v)} step="0.5" />
          </Section>

          <Section title="Loading">
            <NumInput label="EOT Crane Capacity (Tons)" v={s.eot_crane_capacity} onChange={(v) => update('eot_crane_capacity', v)} testid="peb-crane" />
            <NumInput label="Hanging Load (kg/sqm)" v={s.hanging_load_kgsqm} onChange={(v) => update('hanging_load_kgsqm', v)} />
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Wind Speed Zone</label>
              <select value={s.wind_speed} onChange={(e) => update('wind_speed', e.target.value)} className={inputCls + ' bg-white'} data-testid="peb-wind">
                {WIND_ZONES.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
              </select>
            </div>
          </Section>

          <Section title="Mezzanine (optional)">
            <NumInput label="Mezzanine Area (sqm)" v={s.mezzanine_area_sqm} onChange={(v) => update('mezzanine_area_sqm', v)} testid="peb-mezz-area" />
            <NumInput label="Mezzanine Loading (kg/sqm)" v={s.mezzanine_loading_kgsqm} onChange={(v) => update('mezzanine_loading_kgsqm', v)} />
          </Section>

          <Section title="Sheeting & Cladding">
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Roof Sheet</label>
              <select value={s.sheeting_roof_grade} onChange={(e) => updateStr('sheeting_roof_grade', e.target.value)} className={inputCls + ' bg-white'}>
                <option>TR 0.47mm</option>
                <option>TR 0.5mm</option>
                <option>TR 0.6mm</option>
              </select>
            </div>
            <NumInput label="Wall Open % (no sheeting)" v={s.wall_height_open_pct} onChange={(v) => update('wall_height_open_pct', v)} step="5" />
          </Section>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="bg-gradient-to-br from-[#3b4872] to-[#2e3a5d] text-white rounded-sm p-6" data-testid="peb-total">
            <div className="text-[10px] uppercase tracking-widest font-display font-bold opacity-70">Total Estimated Tonnage</div>
            <div className="font-display text-5xl font-extrabold mt-1">{formatT(calc.total)}</div>
            <div className="text-xs opacity-80 mt-2">Built-up Area · {calc.builtUp.toLocaleString('en-IN')} sqm · {formatT(calc.total / calc.builtUp * 1000)} per sqm</div>
            <div className="mt-3 text-xs grid grid-cols-2 gap-2 opacity-90">
              <div>Steel only: <span className="font-bold">{formatT(calc.totalSteel)}</span></div>
              <div>+ Sheeting: <span className="font-bold">{formatT(calc.sheetingWeight)}</span></div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
              <div className="font-display font-bold text-sm">Breakdown</div>
            </div>
            <table className="w-full dense-table">
              <thead>
                <tr>
                  <th>Element</th>
                  <th className="text-right">Rate (kg/sqm)</th>
                  <th className="text-right">Weight</th>
                  <th className="text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                <Row label="Primary Steel (Columns, Rafters)" rate={calc.primaryRate} weight={calc.primary} total={calc.total} testid="peb-primary" />
                <Row label="Secondary Steel (Purlins, Girts, Struts)" rate={calc.secondaryRate} weight={calc.secondary} total={calc.total} testid="peb-secondary" />
                <Row label="Bracing" rate={calc.bracingRate} weight={calc.bracing} total={calc.total} />
                {calc.mezzWeight > 0 && <Row label="Mezzanine Steel" rate={calc.mezzWeight / s.mezzanine_area_sqm} weight={calc.mezzWeight} total={calc.total} />}
                <Row label="Connections & Misc (7%)" rate={null} weight={calc.connections} total={calc.total} />
                <Row label="Roof + Wall Sheeting" rate={null} weight={calc.sheetingWeight} total={calc.total} />
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-display font-bold">
                  <td>TOTAL</td>
                  <td className="text-right">—</td>
                  <td className="text-right text-base">{formatT(calc.total)}</td>
                  <td className="text-right">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
            <div className="flex items-start gap-2 text-xs text-amber-900">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-display font-bold mb-1">Disclaimer · Budget estimation only</div>
                These figures use industry rule-of-thumb formulas. Actual tonnage depends on detailed design per IS-800, IS-875, and IS-1893. Use for tender / preliminary budgeting only. Final tonnage should be confirmed by a structural engineer.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Helper components ─────────────────────────────────────────
function NumInput({ label, v, onChange, step, testid }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
      <input type="number" step={step || '1'} value={v} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
      <div className="kpi-overline">{title}</div>
      {children}
    </div>
  );
}
function Row({ label, rate, weight, total, testid }) {
  return (
    <tr data-testid={testid}>
      <td>{label}</td>
      <td className="text-right text-xs">{rate != null ? rate.toFixed(2) : '—'}</td>
      <td className="text-right font-mono">{(weight / 1000).toFixed(2)} T</td>
      <td className="text-right text-xs">{(weight / total * 100).toFixed(1)}%</td>
    </tr>
  );
}
function SmallStat({ label, value, tone }) {
  const tones = { red: 'text-red-700', blue: 'text-blue-700', emerald: 'text-emerald-700', amber: 'text-amber-700' };
  return (
    <div className="bg-white border border-slate-200 rounded-sm px-3 py-2">
      <div className="text-[9px] uppercase tracking-wider font-display font-bold text-slate-500">{label}</div>
      <div className={`font-display text-lg font-extrabold ${tones[tone] || 'text-[#0F172A]'}`}>{value}</div>
    </div>
  );
}
function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-3 py-1.5 rounded-sm text-xs font-display font-bold uppercase tracking-wider border ${active ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-700 border-slate-300 hover:border-[#3b4872]'}`}>
      {children}
    </button>
  );
}

